// Per-category SEO copy shown on the /kategori/<slug> pages (NOT on the
// /lp/grosir-* ads pages). Keyed by storefront category slug. Written to
// be unique per category. Generic template text across categories reads as thin
// duplicate content and gets down-weighted. Keywords ("pusat grosir Surabaya",
// "grosir <kategori>") are woven in naturally, not stuffed.
//
// The `types` lists and brand/model mentions below were checked against the
// live storefront catalog (Aug 2026). When the assortment changes, update the
// entry instead of letting the copy drift. Entries for hidden categories
// (perlengkapan-bayi, baju-anak, mukena-premium) are kept minimal.

export interface CategoryFaqEntry {
  q: string
  a: string
}

export interface CategoryContent {
  intro: string
  detail: string
  types?: string[]
  /** Overrides the generic buildCategoryFaq template when present. */
  faq?: CategoryFaqEntry[]
  /** Core for brandTitle; keep under ~55 chars. */
  metaTitle?: string
  metaDescription?: string
}

export const CATEGORY_CONTENT: Record<string, CategoryContent> = {
  'kaos-kaki': {
    intro:
      'Kategori kaos kaki Dropify mencakup model untuk wanita, pria, anak, sekolah, olahraga, ibadah, dan bayi. Pembeli grosir dapat membandingkan model serta ukuran sebelum meminta quotation dari Surabaya.',
    detail:
      'Pilihan produk meliputi seri ankle wanita Gelea, kaos kaki bayi dan anak Winteku, kaos kaki sekolah putih dan hitam, kaos kaki jempol muslimah, serta stocking wanita. MOQ kategori kaos kaki umumnya 50 lusin. Ketentuan campur model, ukuran, dan warna perlu dikonfirmasi per produk.',
    types: ['Kaos kaki ankle motif wanita (Gelea)', 'Kaos kaki bayi dan anak (Winteku)', 'Kaos kaki sekolah putih dan hitam', 'Kaos kaki jempol muslimah', 'Kaos kaki pramuka', 'Stocking wanita'],
    metaDescription: undefined,
    faq: [
      {
        q: 'Berapa modal awal untuk kulakan kaos kaki di Dropify?',
        a: 'MOQ kategori kaos kaki umumnya 50 lusin. Nilai pesanan bergantung pada model, susunan varian, jumlah, dan ongkir. Kirim daftar produk kepada admin untuk memperoleh quotation sebelum menentukan modal.',
      },
      {
        q: 'Model apa yang cocok untuk stok pertama toko?',
        a: 'Mulai dari model yang sesuai dengan pelanggan toko Anda. Sampaikan kelompok usia, penggunaan, dan jumlah yang direncanakan agar admin dapat menunjukkan produk yang relevan untuk dibandingkan.',
      },
      {
        q: 'Apakah ada kaos kaki sekolah, pramuka, dan muslimah?',
        a: 'Ada. Katalog memuat kaos kaki sekolah putih, kaos kaki pramuka hitam, dan kaos kaki jempol muslimah. Sebutkan jenis dan jumlah lusin agar admin dapat memeriksa stoknya.',
      },
      {
        q: 'Bisakah menggabungkan beberapa model dalam satu pengiriman?',
        a: 'Kirim daftar model beserta jumlah lusin, ukuran, dan warna masing-masing. Admin akan memeriksa ketentuan campuran per produk serta MOQ sebelum menggabungkannya dalam quotation dan satu pengiriman.',
      },
      {
        q: 'Berapa lama kaos kaki sampai setelah transfer?',
        a: 'Admin mengonfirmasi waktu penyiapan setelah pembayaran. Estimasi perjalanan dari Surabaya mengikuti kota tujuan dan layanan ekspedisi.',
      },
    ],
  },
  'manshet-sarung-tangan': {
    intro:
      'Kategori ini memuat manset, sarung lengan, head buff, dan sarung tangan untuk penggunaan yang berbeda. Pilih produk berdasarkan fungsi, bahan, ukuran, dan satuan pembeliannya.',
    detail:
      'Pilihan produk meliputi manset tangan polos, manset jempol Brilles dan Toyupe, sarung lengan untuk jamaah haji dan umroh, head buff, serta sarung tangan kerja bintik Matahari dan Diamond. Sampaikan jenis penggunaan agar admin tidak mencampur produk busana dengan sarung tangan kerja.',
    types: ['Manset tangan polos (hitam, cream, warna)', 'Manset jempol Brilles dan Toyupe', 'Sarung lengan haji dan umroh seri HJ', 'Head buff polos dan motif', 'Sarung tangan kerja Matahari dan Diamond'],
    metaTitle: 'Grosir Manset & Sarung Tangan Surabaya',
    metaDescription:
      'Grosir manset tangan, manset jempol Brilles, sarung lengan haji umroh, serta sarung tangan kerja Matahari dan Diamond dari Surabaya.',
    faq: [
      {
        q: 'Berapa minimum order manset dan sarung tangan?',
        a: 'Manset dihitung minimum 50 lusin, sarung tangan mulai 25 lusin. Keduanya berisi 12 pcs atau pasang per lusin. Beberapa item bisa lebih fleksibel, tanyakan ke admin.',
      },
      {
        q: 'Manset model apa saja yang tersedia?',
        a: 'Manset tangan polos, manset jempol Brilles dan Toyupe, serta manset cincin untuk jamaah tersedia dalam model yang berbeda. Kirim foto referensi jika Anda belum mengetahui nama produknya.',
      },
      {
        q: 'Apakah ada perlengkapan untuk travel haji dan umroh?',
        a: 'Ada sarung lengan panjang dan manset cincin seri HJ. Sebutkan jumlah rombongan, kota tujuan, dan target penerimaan agar admin dapat memeriksa stok serta estimasi pengirimannya.',
      },
      {
        q: 'Apakah sarung tangan kerja juga masuk kategori ini?',
        a: 'Sarung tangan bintik Matahari dan Diamond ada di kategori ini. Untuk kebutuhan pabrik dan proyek dalam jumlah besar, situs ini punya halaman khusus supplier sarung tangan pabrik dengan penjelasan per seri.',
      },
      {
        q: 'Bagaimana meminta harga grosir?',
        a: 'Kirim daftar model dan jumlah lusin melalui WhatsApp. Admin akan memeriksa stok dan mencantumkan harga serta ongkir dalam quotation.',
      },
    ],
  },
  'bra-lingerie': {
    intro:
      'Kategori bra dan lingerie Dropify memuat model dari Polini, Oreo, Breitex, Guojisan, Piaoli, dan Lingcao. Bandingkan nomor model, konstruksi, bahan, serta rentang ukuran sebelum menentukan pesanan.',
    detail:
      'Model wireless, miniset, dan bra dewasa tersedia untuk pembelian grosir. MOQ bra umumnya 25 lusin. Ketentuan campur ukuran, warna, dan model mengikuti produk, sehingga susunannya perlu dikonfirmasi sebelum quotation dibuat.',
    types: ['Bra wireless harian', 'Miniset', 'Bra dewasa', 'Bralette dan model tanpa kawat', 'Lingerie'],
    metaTitle: 'Katalog Bra, Miniset & Lingerie',
    metaDescription:
      'Katalog bra, miniset, dan lingerie dari Polini, Oreo, Breitex, Guojisan, Piaoli, dan Lingcao. Bandingkan nomor model, ukuran, MOQ, dan stok.',
    faq: [
      {
        q: 'Merek bra apa saja yang tersedia di Dropify?',
        a: 'Polini, Oreo, Breitex, Guojisan, Piaoli, dan Lingcao, ditambah berbagai model miniset. Tiap merek punya nomor model sendiri, jadi sebutkan nomornya saat bertanya stok.',
      },
      {
        q: 'Berapa minimum order bra dan lingerie?',
        a: 'MOQ bra umumnya 25 lusin dengan isi 12 pcs per lusin. Kirim nomor model dan jumlah agar admin dapat mengonfirmasi ketentuan serta menyiapkan quotation.',
      },
      {
        q: 'Bagaimana mengetahui model yang tersedia?',
        a: 'Stok bergerak mengikuti produksi. Kirim nomor model atau foto yang diminati, admin membalas dengan foto stok aktual beserta ukuran dan warna yang tersedia.',
      },
      {
        q: 'Apakah ada halaman khusus per merek?',
        a: 'Ada. Bra Oreo, Piaoli, Tally, Fengyuer, dan Daililing masing-masing punya halaman sendiri berisi katalog model dan panduan memilih. Halaman supplier lingerie Surabaya merangkum semuanya.',
      },
      {
        q: 'Bisakah bra digabung dengan CD dan camisol dalam satu order?',
        a: 'Produk dapat dikirim dalam satu pengiriman dari Surabaya. MOQ tetap dihitung per kategori: bra umumnya 25 lusin, sedangkan CD dan underwear umumnya 50 lusin. Kirim daftar kebutuhan agar admin dapat memeriksa susunannya.',
      },
    ],
  },
  'cd-underwear': {
    intro:
      'Kategori CD dan underwear Dropify mencakup produk untuk wanita, pria, dan anak. Nomor model, bahan, potongan, serta ukuran berbeda antarproduk dan perlu dibandingkan sebelum order.',
    detail:
      'CD wanita tersedia dari Tally, Haode, Polini, Piaoli, dan FEMA. Untuk pria tersedia boxer serta celana dalam Lupo dan Haode, sedangkan produk anak dibedakan menurut kelompok usia. Minimum order kategori ini 50 lusin. Konfirmasikan komposisi model, ukuran, dan warna sebelum meminta quotation.',
    types: ['CD wanita Tally, Haode, Polini, Piaoli, FEMA', 'Boxer dan CD pria Lupo dan Haode', 'CD dan boxer anak', 'Korset dan G-string', 'Singlet'],
    metaTitle: 'Katalog CD & Underwear Pria, Wanita & Anak',
    metaDescription:
      'Katalog CD dan underwear wanita, pria, serta anak dari Tally, Haode, Polini, Piaoli, FEMA, dan Lupo. Bandingkan model, ukuran, MOQ, dan stok.',
    faq: [
      {
        q: 'Merek apa saja untuk CD wanita, pria, dan anak?',
        a: 'CD wanita dari Tally, Haode, Polini, Piaoli, dan FEMA. Untuk pria ada Lupo dan boxer Haode. CD serta boxer anak tersedia untuk laki-laki dan perempuan per kelompok usia.',
      },
      {
        q: 'Merek apa yang memiliki beberapa nomor model CD wanita?',
        a: 'Tally memiliki sejumlah nomor model celana dalam wanita, termasuk CD 917 dan CD 5702. Bandingkan bahan, potongan, dan ukuran setiap model sebelum memilih.',
      },
      {
        q: 'Berapa minimum order CD dan underwear?',
        a: 'MOQ kategori CD dan underwear umumnya 50 lusin dengan isi 12 pcs per lusin. Kirim daftar model dan jumlah untuk memperoleh quotation.',
      },
      {
        q: 'Apakah CD pria, wanita, dan anak bisa satu pengiriman?',
        a: 'Bisa, semuanya satu kategori dan dikirim bersamaan dari Surabaya. Tulis jumlah per jenis supaya stok masing-masing dicek.',
      },
      {
        q: 'Di mana melihat penjelasan lengkap kategori ini?',
        a: 'Halaman grosir underwear Surabaya membahas ketiga lini beserta MOQ dan cara ordernya. CD Tally, CD Piaoli, dan CD FEMA juga punya halaman khusus per merek.',
      },
    ],
  },
  'piyama-oneset': {
    intro:
      'Kategori piyama dan oneset Dropify memuat baju tidur anak, homedress, daster, dan piyama dewasa. Motif serta ukuran berubah menurut model dan periode produksi.',
    detail:
      'Baju tidur anak tersedia dalam motif seperti hiu, gajah, dan buaya. Untuk dewasa terdapat homedress serta piyama Leisure. Minta foto, ukuran, bahan, satuan pembelian, dan MOQ untuk model yang akan dimasukkan ke katalog toko.',
    types: ['Baju tidur anak motif karakter', 'Piyama anak setelan', 'Homedress dewasa', 'Piyama Leisure', 'Daster'],
    metaTitle: 'Grosir Piyama & Baju Tidur Surabaya',
    metaDescription:
      'Grosir piyama, baju tidur anak motif karakter, dan homedress dewasa dari Surabaya. Cek model, ukuran, MOQ, stok, dan quotation melalui WhatsApp.',
    faq: [
      {
        q: 'Jenis piyama apa saja yang bisa dikulak?',
        a: 'Baju tidur anak motif karakter, piyama setelan anak, homedress dan daster dewasa, serta piyama merek Leisure. Minta foto pilihan model yang tersedia kepada admin.',
      },
      {
        q: 'Berapa harga grosir piyama di Dropify?',
        a: 'Harga mengikuti model dan jumlah pembelian. Kirim kode produk serta jumlah yang direncanakan agar admin dapat menyiapkan quotation.',
      },
      {
        q: 'Berapa minimum order untuk kategori ini?',
        a: 'Ketentuan minimum order piyama berbeda dari kategori lusinan lain. Cek halaman minimum order atau tanyakan kepada admin dengan menyebut model yang Anda pilih.',
      },
      {
        q: 'Motif anak apa yang tersedia sekarang?',
        a: 'Katalog mencakup motif binatang seperti hiu, gajah, dan buaya. Minta foto model yang tersedia, lalu sebutkan ukuran dan jumlah yang dibutuhkan.',
      },
      {
        q: 'Apa yang perlu dikirim saat meminta quotation piyama?',
        a: 'Kirim kode atau foto model, ukuran, jumlah, kota tujuan, dan target penerimaan. Admin akan memeriksa stok serta estimasi penyiapannya.',
      },
    ],
  },
  'camisol-longtorso': {
    intro:
      'Camisol dan longtorso digunakan sebagai lapisan dalam untuk kebaya, kemeja tipis, atau seragam. Katalog Dropify memuat produk dari beberapa merek dengan potongan dan ukuran yang berbeda.',
    detail:
      'Camisol Polini tersedia dalam beberapa nomor model seri A. Longtorso tersedia dari Siaozi, Jinnisi, dan Lianglishenduan, sedangkan lini torso mencakup Victoria, Graziella, dan Bellanova. Kirim nomor model serta ukuran saat meminta quotation.',
    types: ['Camisol Polini seri A', 'Kamisol Polini In', 'Longtorso Siaozi dan Jinnisi', 'Torso Victoria dan Graziella', 'Longtorso Lianglishenduan'],
    metaTitle: 'Grosir Camisol & Longtorso Surabaya',
    metaDescription:
      'Grosir camisol Polini dan longtorso dari distributor tangan pertama Surabaya. 25 model dalaman untuk toko pakaian dalam dan butik, bisa digabung dengan order bra dan CD.',
    faq: [
      {
        q: 'Apa beda camisol dan longtorso?',
        a: 'Camisol adalah atasan dalaman dengan tali, dipakai melapisi pakaian tipis. Longtorso potongannya lebih panjang sampai menutup pinggul, biasa dipakai di balik kebaya. Keduanya tersedia di kategori ini.',
      },
      {
        q: 'Merek apa saja yang tersedia?',
        a: 'Camisol tersedia dari Polini, sedangkan longtorso dan torso tersedia dari beberapa lini seperti Siaozi, Jinnisi, Victoria, Graziella, dan Bellanova.',
      },
      {
        q: 'Berapa minimum order camisol dan longtorso?',
        a: 'Ketentuan minimum order kategori ini tanyakan ke admin bersama nomor model yang diminati, atau lihat halaman minimum order untuk gambaran per kategori.',
      },
      {
        q: 'Bisakah digabung dengan order bra dan CD?',
        a: 'Bisa. Produk dapat disatukan dalam satu pengiriman dari Surabaya setelah MOQ setiap kategori dan susunan pesanannya dikonfirmasi.',
      },
      {
        q: 'Bagaimana mengecek bahan sebelum order banyak?',
        a: 'Pesan sample per pcs dengan harga satuan plus ongkir. Bandingkan bahan dan jatuhnya di badan antara camisol dan longtorso sebelum menentukan komposisi order.',
      },
    ],
  },
  'handuk': {
    intro:
      'Kategori handuk Dropify mencakup seri mandi katun, motif sarang lebah, warna polos, dan produk Breitex. Pembeli perlu membandingkan ukuran, bahan, gramasi jika tersedia, dan warna sebelum order.',
    detail:
      'Pilihan produk meliputi handuk mandi katun bermotif sarang lebah, seri HDK, handuk polos, dan Breitex. Toko rumah tangga, penginapan, atau reseller dapat mengirim kebutuhan ukuran, warna, jumlah, serta kota tujuan untuk meminta quotation.',
    types: ['Handuk mandi katun sarang lebah', 'Handuk katun seri HDK', 'Handuk polos warna', 'Handuk Breitex'],
    metaTitle: 'Grosir Handuk Katun Surabaya',
    metaDescription:
      'Grosir handuk mandi katun dari distributor tangan pertama Surabaya. 100+ pilihan termasuk motif sarang lebah dan seri HDK, untuk toko rumah tangga, penginapan, dan reseller.',
    faq: [
      {
        q: 'Jenis handuk apa yang tersedia?',
        a: 'Tersedia handuk mandi katun bermotif sarang lebah, seri HDK, handuk polos, dan Breitex. Minta foto serta data varian yang tersedia sebelum memilih.',
      },
      {
        q: 'Apakah cocok untuk kebutuhan kos atau penginapan?',
        a: 'Untuk kos atau penginapan, kirim ukuran, bahan, warna, jumlah kamar, dan frekuensi penggantian. Admin dapat memakai data tersebut untuk menyiapkan pilihan yang perlu Anda periksa.',
      },
      {
        q: 'Berapa minimum order handuk?',
        a: 'Ketentuan minimum order kategori handuk tanyakan ke admin bersama varian yang diminati, atau cek halaman minimum order untuk gambaran umum per kategori.',
      },
      {
        q: 'Bisakah melihat warna asli sebelum order?',
        a: 'Bisa. Admin dapat mengirim foto stok per warna. Anda juga dapat meminta sample dengan biaya produk dan ongkir untuk memeriksa bahan, jahitan, serta daya serap.',
      },
      {
        q: 'Dari mana handuk dikirim dan berapa lama?',
        a: 'Pesanan dikirim dari Surabaya. Admin mengonfirmasi waktu penyiapan setelah pembayaran, lalu memberi estimasi perjalanan berdasarkan kota tujuan dan layanan ekspedisi.',
      },
    ],
  },
  'keset-karpet': {
    intro:
      'Keset dan karpet dibedakan menurut ukuran, bahan, permukaan, serta motif. Periksa detail tersebut dan target penggunaan sebelum menentukan stok untuk toko.',
    detail:
      'Katalog mencakup keset lantai ukuran 40 x 60 cm, keset dan karpet crystal velvet bermotif minimalis atau karakter, serta keset bulu. Periksa ukuran, bahan permukaan, lapisan bawah, motif, dan cara pengemasan per seri.',
    types: ['Keset lantai 40x60', 'Keset crystal velvet minimalis', 'Keset dan karpet motif karakter', 'Keset bulu', 'Keset motif mix'],
    metaTitle: 'Grosir Keset & Karpet Surabaya',
    metaDescription:
      'Grosir keset lantai 40 x 60 cm, keset crystal velvet, dan karpet karakter dari Surabaya. Cek bahan, motif, MOQ, stok, dan quotation melalui WhatsApp.',
    faq: [
      {
        q: 'Keset ukuran berapa yang tersedia?',
        a: 'Keset lantai kami umumnya berukuran 40x60 cm dalam beberapa seri. Untuk karpet karakter ukurannya lebih besar, minta detail per model ke admin.',
      },
      {
        q: 'Apa itu bahan crystal velvet?',
        a: 'Crystal velvet adalah bahan berbulu halus dan lembut yang dipakai di sebagian besar keset dan karpet motif kami, baik motif minimalis maupun karakter.',
      },
      {
        q: 'Apakah motif bisa dipilih atau datang campur?',
        a: 'Sebagian seri dijual per motif, sebagian dikemas campur motif dalam satu kelompok. Sebutkan seri yang diminati supaya admin menjelaskan cara pengemasannya.',
      },
      {
        q: 'Berapa minimum order keset dan karpet?',
        a: 'Tanyakan ketentuannya kepada admin dengan menyebut seri dan jumlah, atau lihat halaman minimum order. Ongkir dihitung dari isi pesanan, kemasan, layanan ekspedisi, dan kota tujuan.',
      },
      {
        q: 'Cocok dijual di mana?',
        a: 'Keset dapat dijual oleh toko perlengkapan rumah tangga, toserba, dan toko online. Pilih ukuran serta motif berdasarkan pelanggan yang dilayani toko Anda.',
      },
    ],
  },
  'perlengkapan-medis': {
    intro:
      'Kategori ini berisi sarung tangan medis latex dan nitrile per box serta masker COMET untuk rumah sakit, klinik, laboratorium, dan toko alkes. Dropify menyuplainya langsung sebagai distributor tangan pertama di Surabaya.',
    detail:
      'Sarung tangan medis tersedia mulai 10 box dengan pilihan latex dan nitrile, dan masker COMET tersedia per box sesuai tipenya. Penjelasan lengkap spesifikasi, dokumen, dan cara pengadaan ada di halaman perlengkapan medis grosir di situs ini.',
    types: ['Sarung tangan latex per box', 'Sarung tangan nitrile per box', 'Masker COMET Theo'],
    metaTitle: 'Grosir Perlengkapan Medis Surabaya',
    metaDescription:
      'Kategori perlengkapan medis Dropify: sarung tangan latex dan nitrile per box serta masker COMET, mulai 10 box, untuk rumah sakit, klinik, dan toko alkes.',
    faq: [
      {
        q: 'Berapa minimum order sarung tangan medis?',
        a: 'Mulai 10 box untuk sarung tangan medis latex dan nitrile. Sebutkan ukuran dan jumlah box yang dibutuhkan saat menghubungi admin.',
      },
      {
        q: 'Apakah dokumen pengadaan tersedia?',
        a: 'Dokumen berbeda per produk. Sampaikan syarat dari instansi atau tim pengadaanmu, admin memeriksa dokumen yang menyertai stok sebelum PO dibuat.',
      },
      {
        q: 'Di mana penjelasan lengkap produk medis Dropify?',
        a: 'Halaman perlengkapan medis grosir di situs ini membahas tiap produk, isi per box, dan proses pengadaannya, termasuk untuk rumah sakit dan klinik.',
      },
    ],
  },
  masker: {
    intro:
      'Kategori masker Dropify mencakup COMET Theo, OneMed KN95, MOUSON KN95, Orlee Duckbill, dan Buff Masker Katun. Pilih tipe dan kemasan sesuai kebutuhan toko atau tim pengadaan Anda.',
    detail:
      'COMET Theo memiliki kemasan 20 atau 50 pcs sesuai tipe. OneMed KN95 putih dan hitam berisi 20 pcs per box; isi varian cream perlu dikonfirmasi. MOUSON KN95 putih dan Orlee Duckbill 3 ply masing-masing berisi 50 pcs per box. MOUSON berlabel non-medical. Buff Masker Katun dihitung per lusin. Kirim tipe, warna, jumlah, dan kebutuhan dokumen sebelum meminta quotation.',
    types: ['COMET Theo 3 Ply, 4D, KN95, dan Carbon', 'OneMed KN95 putih, hitam, dan cream', 'MOUSON KN95 putih, box 50 pcs, non-medical', 'Orlee Duckbill 3 ply, box 50 pcs', 'Buff Masker Katun per lusin'],
    metaTitle: 'Grosir Masker Surabaya: Pilihan per Box dan Lusin',
    metaDescription:
      'Grosir masker COMET, OneMed, MOUSON, Orlee, dan Buff dari Surabaya. Bandingkan isi kemasan, lalu cek MOQ, stok, dokumen, dan quotation via WhatsApp.',
    faq: [
      {
        q: 'Produk masker apa saja yang tersedia?',
        a: 'Tersedia COMET Theo, OneMed KN95, MOUSON KN95, Orlee Duckbill 3 ply, dan Buff Masker Katun. Stok tipe dan warna dikonfirmasi admin saat pemesanan.',
      },
      {
        q: 'Berapa isi satu box masker COMET Theo?',
        a: 'Model 3 Ply Earloop dan 4 Ply Carbon berisi 50 pcs per box. Model 4D dan KN95 berisi 20 pcs per box.',
      },
      {
        q: 'Berapa minimum order masker?',
        a: 'Minimum pemesanan mengikuti tipe dan satuan masker. COMET, OneMed, MOUSON, dan Orlee dijual per box; Buff Masker Katun per lusin. Kirim nama produk dan jumlah agar admin dapat memeriksa ketentuan, stok, dan harga saat quotation dibuat.',
      },
      {
        q: 'Data apa yang perlu dikirim untuk meminta quotation masker?',
        a: 'Kirim nama tipe, warna bila tersedia, jumlah box, kota tujuan, target penerimaan, dan daftar dokumen yang diwajibkan.',
      },
    ],
  },
  'mukena-premium': {
    intro:
      'Kategori mukena memuat model seperti Alisya, Azizah, Lavender, Renda Mutiara, dan mukena terusan. Periksa bahan, ukuran, detail hiasan, isi kemasan, dan warna setiap model.',
    detail:
      'Toko perlengkapan muslim, penjual hampers, dan toko online dapat meminta katalog serta quotation. Sampaikan target penerimaan jika pesanan disiapkan untuk Ramadan atau Lebaran.',
    types: ['Mukena Alisya', 'Mukena Azizah', 'Mukena Lavender', 'Mukena Renda dan Renda Mutiara', 'Mukena Terusan'],
  },
  'baju-anak': {
    intro:
      'Kategori baju anak memuat singlet, dress, sleepsuit, romper, dan kaos dengan ukuran serta motif yang berbeda per model.',
    detail:
      'Pilihan produk mencakup singlet anak, dress ruffle dan printing Winteku, sleepsuit, romper, serta kaos anak. Kirim model, ukuran, jumlah, dan kota tujuan agar admin dapat memeriksa stok serta menyiapkan quotation.',
    types: ['Singlet anak', 'Dress ruffle dan printing', 'Sleepsuit dan romper', 'Kaos anak'],
  },
  'perlengkapan-bayi': {
    intro:
      'Perlengkapan bayi mencakup produk satuan dan set dengan isi, bahan, serta ukuran yang berbeda. Konfirmasikan rincian setiap set sebelum memasukkannya ke katalog toko.',
    detail:
      'Tersedia set perlengkapan bayi bernomor seri, hampers gift box bayi, dan bantal menyusui dalam berbagai motif. Kami menyuplai toko baby, toko perlengkapan ibu dan anak, sampai penjual online.',
    types: ['Set perlengkapan bayi', 'Hampers gift box bayi', 'Bantal menyusui'],
  },
}

// Fallback for categories without hand-written copy. Uses the category name so
// each page still reads uniquely rather than sharing one identical block.
export function getCategoryContent(slug: string, name: string): CategoryContent {
  const existing = CATEGORY_CONTENT[slug]
  if (existing) return existing

  const label = name.trim().toLowerCase()
  return {
    intro: `Dropify melayani pembelian grosir ${label} dari Surabaya. Pilih produk dari katalog, lalu kirim jumlah, varian, dan kota tujuan untuk meminta quotation.`,
    detail: `Sebagai distributor tangan pertama tanpa perantara, kami menyuplai grosir ${label} ke toko grosir, toko retail, toserba, sampai penjual online, dengan pengiriman ke seluruh Indonesia mulai dari Pulau Jawa, Kalimantan, Sulawesi, sampai NTT.`,
  }
}
